import { environment } from '@src/environments/environment';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { providePrimeNG } from 'primeng/config';
import { definePreset, palette } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';

import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { CustomTranslateService } from '@app/shared/services/custom.translate.service';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';

import { AppRoutingModule } from '@app/app-routing.module';
import { AppComponent } from '@app/app.component';

import '@angular/common/locales/global/en';
import '@angular/common/locales/global/es';
import '@angular/common/locales/global/de';
import '@angular/common/locales/global/fr';

// UI kit
import { UiKitModule } from '@app/core/components/ui-kit/ui-kit.module';

import { LoginComponent } from '@src/app/features/login/login.component';
import { OtpTransparentLoginComponent } from '@src/app/features/otp-transparent-login/otp-transparent-login.component'; // transparent login flow
import { ResetPasswordComponent } from '@src/app/features/reset-password/reset-password.component';
import { NewPasswordComponent } from '@src/app//features/new-password/new-password.component';
import { ChangePasswordFormComponent } from '@src/app/shared/components/change-password-form/change-password-form.component';
import { LandingComponent } from '@src/app/features/landing/landing.component';
import { DigitalPassportComponent } from '@src/app/features/digital-passport/digital-passport.component';
import { DigitalCardPasscodeComponent } from '@src/app/shared/components/digital-card-passcode/digital-card-passcode.component';
import { PageNotFoundComponent } from '@src/app/features/page-not-found/page-not-found.component';

// WYSIWYG editor
import { QuillModule } from 'ngx-quill';

// Apis
import { ApiModule as ClientApiModule } from '@app/shared/backend/clients';
import { ApiModule as MfaApiModule } from '@app/shared/backend/mfa';
import { Configuration as ClientConfiguration } from '@app/shared/backend/clients';
import { Configuration as MfaConfiguration } from '@app/shared/backend/mfa';
import { AuthInterceptor } from '@app/shared/services/auth.interceptor';

import { ToasterComponent } from '@src/app/shared/components/toaster/toaster.component';
import { ToasterConfigComponent } from '@src/app/shared/components/toaster/toaster-config.component';
import { CountryService } from '@app/shared/services/countries-service';
import { ErrorHandlingInterceptor } from '@app/shared/services/errorHandling.interceptor';
import { PublicLayoutComponent } from '@app/core/components/base-layout/public-layout/public-layout.component';
import { PrivateLayoutComponent } from '@app/core/components/base-layout/private-layout/private-layout.component';
import { DisabledComponent } from './features/disabled/disabled.component';
import { BlankComponent } from './features/card-orders/blank/blank.component';
import { AddCardOrderComponent } from './features/card-orders/add-card-order/add-card-order.component';
import { CardOrdersComponent } from './features/card-orders/card-orders/card-orders.component';
import { ViewCardOrderComponent } from './features/card-orders/view-card-order/view-card-order.component';
import { AddCategoryComponent } from './features/categories/add-category/add-category.component';
import { CategoriesComponent } from './features/categories/categories/categories.component';
import { EditCategoryComponent } from './features/categories/edit-category/edit-category.component';
import { ViewCategoryComponent } from './features/categories/view-category/view-category.component';
import { AddCodeOrderComponent } from './features/code-orders/add-code-order/add-code-order.component';
import { CodeOrdersComponent } from './features/code-orders/code-orders/code-orders.component';
import { ViewCodeOrderComponent } from './features/code-orders/view-code-order/view-code-order.component';
import { AddCollectionComponent } from './features/collections/add-collection/add-collection.component';
import { CollectionsComponent } from './features/collections/collections/collections.component';
import { EditCollectionComponent } from './features/collections/edit-collection/edit-collection.component';
import { ViewCollectionComponent } from './features/collections/view-collection/view-collection.component';
import { AddCustomerComponent } from './features/customers/add-customer/add-customer.component';
import { CustomersComponent } from './features/customers/customers/customers.component';
import { EditCustomerComponent } from './features/customers/edit-customer/edit-customer.component';
import { ViewCustomerComponent } from './features/customers/view-customer/view-customer.component';
import { AddDigitalCardComponent } from './features/digital-cards/add-digital-card/add-digital-card.component';
import { ViewDigitalCardComponent } from './features/digital-cards/view-digital-card/view-digital-card.component';
import { AddDistributionChannelComponent } from './features/distribution-channels/add-distribution-channel/add-distribution-channel.component';
import { DistributionChannelsComponent } from './features/distribution-channels/distribution-channels/distribution-channels.component';
import { EditDistributionChannelComponent } from './features/distribution-channels/edit-distribution-channel/edit-distribution-channel.component';
import { ViewDistributionChannelComponent } from './features/distribution-channels/view-distribution-channel/view-distribution-channel.component';
import { ImportsComponent } from './features/imports/imports/imports.component';
import { UploadDataComponent } from './features/imports/upload-data/upload-data.component';
import { AddInventoryComponent } from './features/inventories/add-inventory/add-inventory.component';
import { EditInventoryComponent } from './features/inventories/edit-inventory/edit-inventory.component';
import { InventoriesComponent } from './features/inventories/inventories/inventories.component';
import { ViewInventoryComponent } from './features/inventories/view-inventory/view-inventory.component';
import { AddItemComponent } from './features/items/add-item/add-item.component';
import { EditItemComponent } from './features/items/edit-item/edit-item.component';
import { ItemsComponent } from './features/items/items/items.component';
import { ViewItemComponent } from './features/items/view-item/view-item.component';
import { AddMarketComponent } from './features/markets/add-market/add-market.component';
import { EditMarketComponent } from './features/markets/edit-market/edit-market.component';
import { MarketsComponent } from './features/markets/markets/markets.component';
import { ViewMarketComponent } from './features/markets/view-market/view-market.component';
import { OtpErrorComponent } from './features/otp/otp-error/otp-error.component';
import { OtpSetupComponent } from './features/otp/otp-setup/otp-setup.component';
import { OtpVerifiedComponent } from './features/otp/otp-verified/otp-verified.component';
import { AddProductAttributeDefinitionsComponent } from './features/product-attribute-definitions/add-product-attribute-definitions/add-product-attribute-definitions.component';
import { EditProductAttributeDefinitionsComponent } from './features/product-attribute-definitions/edit-product-attribute-definitions/edit-product-attribute-definitions.component';
import { ProductAttributeDefinitionsComponent } from './features/product-attribute-definitions/product-attribute-definitions/product-attribute-definitions.component';
import { ViewProductAttributeDefinitionsComponent } from './features/product-attribute-definitions/view-product-attribute-definitions/view-product-attribute-definitions.component';
import { AddProductCustomizationsComponent } from './features/product-customizations/add-product-customizations/add-product-customizations.component';
import { EditProductCustomizationsComponent } from './features/product-customizations/edit-product-customizations/edit-product-customizations.component';
import { ProductCustomizationsComponent } from './features/product-customizations/product-customizations/product-customizations.component';
import { ViewProductCustomizationsComponent } from './features/product-customizations/view-product-customizations/view-product-customizations.component';
import { AddProductTypeComponent } from './features/product-types/add-product-type/add-product-type.component';
import { EditProductTypeComponent } from './features/product-types/edit-product-type/edit-product-type.component';
import { ProductTypesComponent } from './features/product-types/product-types/product-types.component';
import { ViewProductTypeComponent } from './features/product-types/view-product-type/view-product-type.component';
import { AddProductComponent } from './features/products/add-product/add-product.component';
import { EditProductComponent } from './features/products/edit-product/edit-product.component';
import { ProductsComponent } from './features/products/products/products.component';
import { ViewProductComponent } from './features/products/view-product/view-product.component';
import { AddRepairComponent } from './features/repair/add-repair/add-repair.component';
import { AddRetailerTypeComponent } from './features/retailer-types/add-retailer-type/add-retailer-type.component';
import { EditRetailerTypeComponent } from './features/retailer-types/edit-retailer-type/edit-retailer-type.component';
import { RetailerTypesComponent } from './features/retailer-types/retailer-types/retailer-types.component';
import { ViewRetailerTypeComponent } from './features/retailer-types/view-retailer-type/view-retailer-type.component';
import { ManageRetailerUsersComponent } from './features/retailers/manage-retailer-users/manage-retailer-users.component';
import { AddRetailerComponent } from './features/retailers/add-retailer/add-retailer.component';
import { EditRetailerComponent } from './features/retailers/edit-retailer/edit-retailer.component';
import { RetailersComponent } from './features/retailers/retailers/retailers.component';
import { ViewRetailerComponent } from './features/retailers/view-retailer/view-retailer.component';
import { AddSaleComponent } from './features/sales/add-sale/add-sale.component';
import { StepCustomerDetailsComponent } from './features/sales/add-sale/steps/customer-details/customer-details.component';
import { StepSaleSummaryComponent } from './features/sales/add-sale/steps/sale-summary/sale-summary.component';
import { StepSaleTypeComponent } from './features/sales/add-sale/steps/sale-type/sale-type.component';
import { StepSellerDetailsComponent } from './features/sales/add-sale/steps/seller-details/seller-details.component';
import { StepSerialNumberValidationComponent } from './features/sales/add-sale/steps/serial-number-validation/serial-number-validation.component';
import { StepSaleWarrantyCodeValidationComponent } from './features/sales/add-sale/steps/warranty-code-validation/warranty-code-validation.component';
import { EditSaleComponent } from './features/sales/edit-sale/edit-sale.component';
import { SalesComponent } from './features/sales/sales/sales.component';
import { ViewSaleComponent } from './features/sales/view-sale/view-sale.component';
import { AddShipmentComponent } from './features/shipments/add-shipment/add-shipment.component';
import { EditShipmentComponent } from './features/shipments/edit-shipment/edit-shipment.component';
import { ShipmentsComponent } from './features/shipments/shipments/shipments.component';
import { ViewShipmentComponent } from './features/shipments/view-shipment/view-shipment.component';
import { AddUserComponent } from './features/users/add-user/add-user.component';
import { EditUserComponent } from './features/users/edit-user/edit-user.component';
import { UsersComponent } from './features/users/users/users.component';
import { ViewUserComponent } from './features/users/view-user/view-user.component';
import { AddUnregisteredUserComponent } from './features/unregistered-users/add-unregistered-user/add-unregistered-user.component';
import { UnregisteredUsersComponent } from './features/unregistered-users/unregistered-users/unregistered-users.component'
import { EditUnregisteredUserComponent } from './features/unregistered-users/edit-unregistered-user/edit-unregistered-user.component';
import { ActivateWarrantyProcessComponent } from './features/warranties/activate-warranty-process/activate-warranty-process.component';
import { StepActivateWarrantyComponent } from './features/warranties/activate-warranty-process/steps/activate-warranty/activate-warranty.component';
import { StepActivationFeedbackComponent } from './features/warranties/activate-warranty-process/steps/activation-feedback/activation-feedback.component';
import { StepActivationMethodComponent } from './features/warranties/activate-warranty-process/steps/activation-method/activation-method.component';
import { StepRetailerSelectionComponent } from './features/warranties/activate-warranty-process/steps/retailer-selection/retailer-selection.component';
import { StepSerialNumberSearchComponent } from './features/warranties/activate-warranty-process/steps/serial-number-search/serial-number-search.component';
import { StepWarrantyCodeValidationComponent } from './features/warranties/activate-warranty-process/steps/warranty-code-validation/warranty-code-validation.component';
import { EditWarrantyComponent } from './features/warranties/edit-warranty/edit-warranty.component';
import { ViewWarrantyComponent } from './features/warranties/view-warranty/view-warranty.component';
import { WarrantiesComponent } from './features/warranties/warranties/warranties.component';
import { BusinessGroupsComponent } from '@src/app/features/business-groups/business-groups/business-groups.component';
import { ViewBusinessGroupComponent } from '@app/features/business-groups/view-business-group/view-business-group.component';
import { EditBusinessGroupComponent } from '@src/app/features/business-groups/edit-business-group/edit-business-group.component';
import { CardOrderingRoutingModule } from './features/card-orders/card-orders-routing.module';
import { CategoryRoutingModule } from './features/categories/category-routing.module';
import { CodeOrdersRoutingModule } from './features/code-orders/code-orders-routing.module';
import { CollectionsRoutingModule } from './features/collections/collection-routing.module';
import { CustomersRoutingModule } from './features/customers/customers-routing.module';
import { DigitalCardsRoutingModule } from './features/digital-cards/digital-cards-routing.module';
import { DistributionChannelsRoutingModule } from './features/distribution-channels/distribution-channels-routing.module';
import { ImportsRoutingModule } from './features/imports/imports-routing.module';
import { InventoriesRoutingModule } from './features/inventories/inventories-routing.module';
import { ItemsRoutingModule } from './features/items/items-routing.module';
import { MarketRoutingModule } from './features/markets/market-routing.module';
import { OtpRoutingModule } from './features/otp/otp-routing.module';
import { ProductAttributeDefinitionsRoutingModule } from './features/product-attribute-definitions/product-attribute-definitions-routing.module';
import { ProductCustomizationsRoutingModule } from './features/product-customizations/product-customizations-routing.module';
import { ProductTypeRoutingModule } from './features/product-types/product-type-routing.module';
import { ProductsRoutingModule } from './features/products/products-routing.module';
import { RepairsRoutingModule } from './features/repair/repair-routing.module';
import { RetailerTypesRoutingModule } from './features/retailer-types/retailer-type-routing.module';
import { RetailersRoutingModule } from './features/retailers/retailers-routing.module';
import { SalesRoutingModule } from './features/sales/sales-routing.module';
import { ShipmentsRoutingModule } from './features/shipments/shipments-routing.module';
import { UsersRoutingModule } from './features/users/users-routing.module';
import { BusinessGroupsRoutingModule } from '@src/app/features/business-groups/business-groups-routing.module';
import { AddBusinessGroupComponent } from '@src/app/features/business-groups/add-business-group/add-business-group.component';
import { ViewUnregisteredUserComponent } from '@src/app/features/unregistered-users/view-unregistered-user/view-unregistered-user.component';
import { SurveysRoutingModule } from '@src/app/features/surveys/surveys-routing.module';
import { SurveysComponent } from '@src/app/features/surveys/surveys/surveys.component';
import { AddSurveyComponent } from '@src/app/features/surveys/add-survey/add-survey.component';
import { EditSurveyComponent } from '@src/app/features/surveys/edit-survey/edit-survey.component';
import { ViewSurveyComponent } from '@src/app/features/surveys/view-survey/view-survey.component';
import { TakeSurveyComponent } from '@src/app/features/surveys/take-survey/take-survey.component';
import { SurveySelectorComponent } from '@src/app/features/surveys/survey-selector/survey-selector.component';
import { OtpAuthErrorComponent } from './features/otp-transparent-login/otp-auth-error/otp-auth-error.component';
import { ManageMarketUsersComponent } from '@src/app/features/markets/manage-market-users/manage-market-users.component';
import { ManageMarketRetailersComponent } from '@src/app/features/markets/manage-market-retailers/manage-market-retailers.component';
import { RetailerAttributeDefinitionsRoutingModule } from './features/retailer-attribute-definitions/retailer-attribute-definitions-routing.module';
import { AttributeDefinitionFormComponent } from './shared/components/attribute-definition-form/attribute-definition-form.component';
import { RetailerAttributeDefinitionsComponent } from '@src/app/features/retailer-attribute-definitions/retailer-attribute-definitions/retailer-attribute-definitions.component';
import { AddRetailerAttributeDefinitionsComponent } from '@src/app/features/retailer-attribute-definitions/add-retailer-attribute-definitions/add-retailer-attribute-definitions.component';
import { EditRetailerAttributeDefinitionsComponent } from '@src/app/features/retailer-attribute-definitions/edit-retailer-attribute-definitions/edit-retailer-attribute-definitions.component';
import { ViewRetailerAttributeDefinitionsComponent } from '@src/app/features/retailer-attribute-definitions/view-retailer-attribute-definitions/view-retailer-attribute-definitions.component';
import { AttributeValuesFormComponent } from '@src/app/shared/components/attribute-values-form/attribute-values-form.component';

@NgModule({
  declarations: [
    AppComponent,
    PrivateLayoutComponent,
    PublicLayoutComponent,
    LandingComponent,
    LoginComponent,
    OtpTransparentLoginComponent,
    OtpAuthErrorComponent,
    DisabledComponent,
    ResetPasswordComponent,
    NewPasswordComponent,
    ChangePasswordFormComponent,
    DigitalPassportComponent,
    DigitalCardPasscodeComponent,
    PageNotFoundComponent,
    ToasterComponent,
    ToasterConfigComponent,
    BlankComponent,
    AddCardOrderComponent,
    CardOrdersComponent,
    ViewCardOrderComponent,
    CategoriesComponent,
    AddCategoryComponent,
    EditCategoryComponent,
    ViewCategoryComponent,
    AddCodeOrderComponent,
    CodeOrdersComponent,
    ViewCodeOrderComponent,
    CollectionsComponent,
    AddCollectionComponent,
    EditCollectionComponent,
    ViewCollectionComponent,
    AddCustomerComponent,
    EditCustomerComponent,
    CustomersComponent,
    ViewCustomerComponent,
    AddDigitalCardComponent,
    ViewDigitalCardComponent,
    DistributionChannelsComponent,
    AddDistributionChannelComponent,
    EditDistributionChannelComponent,
    ViewDistributionChannelComponent,
    ImportsComponent,
    UploadDataComponent,
    AddInventoryComponent,
    EditInventoryComponent,
    InventoriesComponent,
    ViewInventoryComponent,
    AddItemComponent,
    EditItemComponent,
    ItemsComponent,
    ViewItemComponent,
    MarketsComponent,
    AddMarketComponent,
    EditMarketComponent,
    ViewMarketComponent,
    OtpSetupComponent,
    OtpVerifiedComponent,
    OtpErrorComponent,
    ProductAttributeDefinitionsComponent,
    AddProductAttributeDefinitionsComponent,
    EditProductAttributeDefinitionsComponent,
    ViewProductAttributeDefinitionsComponent,
    ProductCustomizationsComponent,
    AddProductCustomizationsComponent,
    ViewProductCustomizationsComponent,
    EditProductCustomizationsComponent,
    ProductTypesComponent,
    AddProductTypeComponent,
    EditProductTypeComponent,
    ViewProductTypeComponent,
    AddProductComponent,
    EditProductComponent,
    ProductsComponent,
    ViewProductComponent,
    AddRepairComponent,
    RetailerTypesComponent,
    AddRetailerTypeComponent,
    EditRetailerTypeComponent,
    ViewRetailerTypeComponent,
    AddRetailerComponent,
    ManageRetailerUsersComponent,
    EditRetailerComponent,
    RetailersComponent,
    ViewRetailerComponent,
    AddSaleComponent,
    EditSaleComponent,
    SalesComponent,
    ViewSaleComponent,
    StepSerialNumberValidationComponent,
    StepSaleTypeComponent,
    StepSellerDetailsComponent,
    StepCustomerDetailsComponent,
    StepSaleWarrantyCodeValidationComponent,
    StepSaleSummaryComponent,
    AddShipmentComponent,
    EditShipmentComponent,
    ShipmentsComponent,
    ViewShipmentComponent,
    AddUserComponent,
    EditUserComponent,
    UsersComponent,
    ViewUserComponent,
    ActivateWarrantyProcessComponent,
    EditWarrantyComponent,
    StepActivateWarrantyComponent,
    StepActivationFeedbackComponent,
    StepActivationMethodComponent,
    StepRetailerSelectionComponent,
    StepSerialNumberSearchComponent,
    StepWarrantyCodeValidationComponent,
    ViewWarrantyComponent,
    WarrantiesComponent,
    BusinessGroupsComponent,
    AddBusinessGroupComponent,
    ViewBusinessGroupComponent,
    EditBusinessGroupComponent,
    UnregisteredUsersComponent,
    AddUnregisteredUserComponent,
    EditUnregisteredUserComponent,
    ViewUnregisteredUserComponent,
    SurveysComponent,
    AddSurveyComponent,
    EditSurveyComponent,
    ViewSurveyComponent,
    TakeSurveyComponent,
    SurveySelectorComponent,
    ManageMarketUsersComponent,
    ManageMarketRetailersComponent,
    RetailerAttributeDefinitionsComponent,
    AddRetailerAttributeDefinitionsComponent,
    EditRetailerAttributeDefinitionsComponent,
    ViewRetailerAttributeDefinitionsComponent,
    AttributeDefinitionFormComponent,
    AttributeValuesFormComponent
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    ClientApiModule.forRoot(() => new ClientConfiguration({ basePath: environment.apiClient })),
    MfaApiModule.forRoot(() => new MfaConfiguration({ basePath: environment.mfaClient })),
    HttpClientModule,
    QuillModule.forRoot(),
    UiKitModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateService,
        deps: [HttpClient]
      }
    }),
    CardOrderingRoutingModule,
    CategoryRoutingModule,
    CodeOrdersRoutingModule,
    CollectionsRoutingModule,
    CustomersRoutingModule,
    DigitalCardsRoutingModule,
    DistributionChannelsRoutingModule,
    ImportsRoutingModule,
    InventoriesRoutingModule,
    ItemsRoutingModule,
    MarketRoutingModule,
    OtpRoutingModule,
    ProductAttributeDefinitionsRoutingModule,
    ProductCustomizationsRoutingModule,
    ProductTypeRoutingModule,
    ProductsRoutingModule,
    RepairsRoutingModule,
    RetailerTypesRoutingModule,
    RetailersRoutingModule,
    SalesRoutingModule,
    ShipmentsRoutingModule,
    UsersRoutingModule,
    BusinessGroupsRoutingModule,
    SurveysRoutingModule,
    RetailerAttributeDefinitionsRoutingModule
  ],
  exports: [
    TranslateModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    providePrimeNG({
      theme: {
        preset: definePreset(Lara, {
          semantic: {
            primary: palette('#031d26')
          }
        }),
        options: {
          darkModeSelector: false
        }
      }
    }),
    CountryService,
    { provide: HTTP_INTERCEPTORS, useClass: ErrorHandlingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private translateService: TranslateService) {
    this.translateService.setDefaultLang('en');
    this.translateService.use('en');
  }
}
